/**
 * Document Validation Utilities
 * 
 * Client-side validations to ensure captured images are actual ID documents
 * and not just photos or hand-drawn images.
 */

/**
 * Validates that an image appears to be a legitimate ID document
 * 
 * @param {string} imageDataUrl - Base64 data URL of the image
 * @returns {Promise<{isValid: boolean, reason?: string, confidence: number}>}
 */
export async function validateIdDocument(imageDataUrl) {
  try {
    const image = await loadImage(imageDataUrl);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    
    // Run multiple validation checks
    const checks = {
      hasRectangularShape: checkRectangularShape(imageData),
      hasTextLikePatterns: checkTextPatterns(imageData),
      hasGoodContrast: checkContrast(imageData),
      hasAppropriateSize: checkImageSize(image),
      isNotBlurry: checkSharpness(imageData),
      hasColorVariation: checkColorVariation(imageData)
    };
    
    console.log('=== DOCUMENT VALIDATION CHECKS ===', checks);
    
    // Calculate confidence score (0-100)
    const passedChecks = Object.values(checks).filter(Boolean).length;
    const totalChecks = Object.keys(checks).length;
    const confidence = Math.round((passedChecks / totalChecks) * 100);
    
    // Require at least 2 out of 6 checks to pass
    // Keep this very lenient — the real validation happens server-side with Rekognition
    const isValid = passedChecks >= 2;
    
    let reason = '';
    if (!isValid) {
      const failedChecks = Object.entries(checks)
        .filter(([, passed]) => !passed)
        .map(([check]) => check);
      reason = `La imagen no parece ser un documento de identidad válido. Problemas detectados: ${failedChecks.join(', ')}`;
    }
    
    return {
      isValid,
      reason,
      confidence,
      checks
    };
  } catch (error) {
    console.error('Document validation error:', error);
    return {
      isValid: false,
      reason: 'Error al validar la imagen',
      confidence: 0
    };
  }
}

/**
 * Load image from data URL
 */
function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/**
 * Check if image has rectangular document-like shape
 * Documents typically have strong horizontal and vertical edges
 */
function checkRectangularShape(imageData) {
  const edges = detectEdges(imageData);
  const horizontalEdges = countHorizontalEdges(edges);
  const verticalEdges = countVerticalEdges(edges);
  
  // Documents should have significant horizontal and vertical edges
  return horizontalEdges > 50 && verticalEdges > 50;
}

/**
 * Check if image has text-like patterns
 * Documents contain printed text with specific characteristics
 */
function checkTextPatterns(imageData) {
  const { data, width, height } = imageData;
  let textLikeRegions = 0;
  const sampleSize = 20; // Sample every 20 pixels
  
  for (let y = 0; y < height; y += sampleSize) {
    for (let x = 0; x < width; x += sampleSize) {
      const idx = (y * width + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      
      // Check for high contrast (typical of text)
      const brightness = (r + g + b) / 3;
      if (brightness < 100 || brightness > 200) {
        textLikeRegions++;
      }
    }
  }
  
  const totalSamples = (width / sampleSize) * (height / sampleSize);
  const textRatio = textLikeRegions / totalSamples;
  
  // Documents typically have 20-80% text coverage
  return textRatio > 0.2 && textRatio < 0.8;
}

/**
 * Check if image has good contrast
 * Documents have clear distinction between background and text/images
 */
function checkContrast(imageData) {
  const { data } = imageData;
  let minBrightness = 255;
  let maxBrightness = 0;
  
  for (let i = 0; i < data.length; i += 4) {
    const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
    minBrightness = Math.min(minBrightness, brightness);
    maxBrightness = Math.max(maxBrightness, brightness);
  }
  
  const contrast = maxBrightness - minBrightness;
  
  // Good documents have contrast of at least 100
  return contrast > 100;
}

/**
 * Check if image size is appropriate
 * Too small images are likely not real documents
 */
function checkImageSize(image) {
  const minWidth = 400;
  const minHeight = 250;
  
  return image.width >= minWidth && image.height >= minHeight;
}

/**
 * Check if image is sharp (not blurry)
 * Blurry images might be attempts to hide fake documents
 */
function checkSharpness(imageData) {
  const edges = detectEdges(imageData);
  const { data } = edges;
  
  let edgeStrength = 0;
  for (let i = 0; i < data.length; i += 4) {
    edgeStrength += data[i];
  }
  
  const avgEdgeStrength = edgeStrength / (data.length / 4);
  
  // Sharp images have stronger edges
  return avgEdgeStrength > 20;
}

/**
 * Check if image has color variation
 * Real documents have multiple colors (security features, photos, etc.)
 */
function checkColorVariation(imageData) {
  const { data } = imageData;
  const colorBuckets = new Set();
  
  // Sample colors and bucket them
  for (let i = 0; i < data.length; i += 40) {
    const r = Math.floor(data[i] / 32);
    const g = Math.floor(data[i + 1] / 32);
    const b = Math.floor(data[i + 2] / 32);
    colorBuckets.add(`${r}-${g}-${b}`);
  }
  
  // Real documents have at least 10 distinct color regions
  return colorBuckets.size > 10;
}

/**
 * Simple edge detection using Sobel operator
 */
function detectEdges(imageData) {
  const { data, width, height } = imageData;
  const edges = new ImageData(width, height);
  
  // Sobel kernels
  const sobelX = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
  const sobelY = [-1, -2, -1, 0, 0, 0, 1, 2, 1];
  
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      let gx = 0;
      let gy = 0;
      
      for (let ky = -1; ky <= 1; ky++) {
        for (let kx = -1; kx <= 1; kx++) {
          const idx = ((y + ky) * width + (x + kx)) * 4;
          const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
          const kernelIdx = (ky + 1) * 3 + (kx + 1);
          
          gx += brightness * sobelX[kernelIdx];
          gy += brightness * sobelY[kernelIdx];
        }
      }
      
      const magnitude = Math.sqrt(gx * gx + gy * gy);
      const idx = (y * width + x) * 4;
      edges.data[idx] = magnitude;
      edges.data[idx + 1] = magnitude;
      edges.data[idx + 2] = magnitude;
      edges.data[idx + 3] = 255;
    }
  }
  
  return edges;
}

/**
 * Count horizontal edges (for document shape detection)
 */
function countHorizontalEdges(edges) {
  const { data, width, height } = edges;
  let count = 0;
  const threshold = 50;
  
  for (let y = 0; y < height; y++) {
    let rowEdges = 0;
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      if (data[idx] > threshold) {
        rowEdges++;
      }
    }
    if (rowEdges > width * 0.3) {
      count++;
    }
  }
  
  return count;
}

/**
 * Count vertical edges (for document shape detection)
 */
function countVerticalEdges(edges) {
  const { data, width, height } = edges;
  let count = 0;
  const threshold = 50;
  
  for (let x = 0; x < width; x++) {
    let colEdges = 0;
    for (let y = 0; y < height; y++) {
      const idx = (y * width + x) * 4;
      if (data[idx] > threshold) {
        colEdges++;
      }
    }
    if (colEdges > height * 0.3) {
      count++;
    }
  }
  
  return count;
}
